import { useApi } from "@api/ApiProvider";
import { Document } from "@components/documents/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deleteFileApi, downloadFileApi, editFileApi, uploadFileApi } from "./documents.api";
import { documentsQueryKeyBySession } from "./utils";

export interface UploadFileParams {
  file: File;
  type: string;
  sessionId: string;
  workingDocumentId: string;
  partsList?: File | null;
}

export interface DeleteFileParams {
  sessionId: string;
  documentId: string;
}

export interface EditFileParams {
  sessionId: string;
  documentId: string;
  fileType: string;
  label: string;
  partsList?: string | null;
}

export interface DownloadFileParams {
  sourcePath: string;
}

export const useDocumentsMutations = () => {
  const { api } = useApi();
  const queryClient = useQueryClient();

  const uploadFileMutation = useMutation({
    mutationFn: (params: UploadFileParams) => uploadFileApi({ apiClient: api, ...params }),
    onSuccess: (newDocument, params) => {
      queryClient.setQueryData(documentsQueryKeyBySession(params.sessionId), (oldData: Document[] | undefined) =>
        oldData ? [...oldData, newDocument] : [newDocument]
      );
    },
  });

  const deleteFileMutation = useMutation({
    mutationFn: (params: DeleteFileParams) => deleteFileApi(api, params.sessionId, params.documentId),
    onSuccess: (_, params) => {
      queryClient.setQueryData(documentsQueryKeyBySession(params.sessionId), (oldData: Document[] | undefined) =>
        oldData ? oldData.filter((doc) => doc.id !== params.documentId) : oldData
      );
    },
  });

  const editFileMutation = useMutation({
    mutationFn: (params: EditFileParams) => editFileApi({ apiClient: api, ...params }),
    onSuccess: (extracts, params) => {
      queryClient.setQueryData(documentsQueryKeyBySession(params.sessionId), (oldData: Document[] | undefined) =>
        oldData?.map((doc) =>
          doc.id === params.documentId ? { ...doc, label: params.label, category: params.fileType, extracts } : doc
        )
      );
    },
  });

  const downloadFileMutation = useMutation({
    mutationFn: async (params: DownloadFileParams) => {
      const url = await downloadFileApi(api, params.sourcePath);
      window.open(url, "_blank", "noopener,noreferrer");
    },
  });

  return {
    uploadFileMutation,
    deleteFileMutation,
    editFileMutation,
    downloadFileMutation,
  };
};
